import React from 'react';
import heroImage from '../assets/article-oncology-surrogate-endpoints.png';
import articleBodyImage from '../assets/article-oncology1.png';
import articleClinicalImage from '../assets/article-oncology2.png';

const ArticleOncologySurrogateEndpoints = () => {
  return (
    <div className="bg-white pb-16">
      <section
        className="relative isolate min-h-[38vh] w-full overflow-hidden sm:min-h-[40vh]"
        aria-labelledby="oncology-article-hero-heading"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-950/55 via-slate-950/35 to-slate-950/60"
          aria-hidden
        />
        <div className="relative z-10 mx-auto flex min-h-[38vh] max-w-6xl flex-col items-center justify-center px-4 py-12 sm:min-h-[40vh] sm:px-6 sm:py-14 lg:px-8">
          <div className="w-full max-w-3xl text-center">
            <h1
              id="oncology-article-hero-heading"
              className="text-pretty text-3xl font-semibold tracking-tight text-white sm:text-4xl lg:text-5xl"
            >
              Interpreting surrogate endpoints in oncology trials
            </h1>
            <p className="mt-4 text-sm text-slate-100/95 sm:text-base">
              <span className="text-slate-100">By Dr. Ananya Sharma</span>
              <span className="mx-2 text-slate-300/90" aria-hidden>
                ·
              </span>
              <time dateTime="2026-05-02" className="tabular-nums text-slate-100/90">
                May 2, 2026
              </time>
            </p>
          </div>
        </div>
      </section>

      <section
        className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-12"
        aria-labelledby="oncology-article-intro-heading"
      >
        <div className="grid grid-cols-1 gap-8 md:grid-cols-[minmax(0,1.08fr)_minmax(0,0.92fr)] md:items-stretch md:gap-8 lg:gap-10">
          <div className="min-h-0 w-full md:h-full">
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl shadow-lg ring-1 ring-slate-200/80 md:aspect-auto md:h-full md:min-h-0">
              <img
                src={articleBodyImage}
                alt="Medical tray with gauze, a syringe, and vials on a clinical drape"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
          <div className="min-h-0 min-w-0 space-y-5 text-base leading-relaxed text-slate-600">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-brand-700">What surrogates capture</p>
              <h2
                id="oncology-article-intro-heading"
                className="mt-2 text-pretty text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl"
              >
                What surrogates measure, and what they do not
              </h2>
            </div>
            <p>
              Surrogate endpoints (such as progression-free survival or tumour shrinkage) can speed trials and give an
              early read on whether a drug is doing something measurable. They are not the same as proof that patients
              live longer or feel better.
            </p>
            <p>
              This piece walks through how regulators and clinicians weigh surrogates, what validation means in practice,
              and how to read an oncology paper when overall survival is not the headline outcome.
            </p>
            <p>
              Validation is rarely a single yes-or-no stamp. It usually means asking whether changes in the surrogate have
              tracked with changes in outcomes that matter to patients in prior studies and settings, and whether that
              link is strong enough to justify decisions for a new drug or a new population.
            </p>
            <p>
              When you read a trial, look past the abstract: how progression was defined and reviewed, how long follow-up
              ran for survival endpoints, and whether secondary analyses or crossover could blur the picture. Those
              details often decide how much weight a surrogate result should carry.
            </p>
          </div>
        </div>
      </section>

      <section
        className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-12"
        aria-labelledby="oncology-article-clinical-heading"
      >
        <div className="grid grid-cols-1 gap-8 md:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] md:items-stretch md:gap-8 lg:gap-10">
          <div className="min-h-0 min-w-0 space-y-5 text-base leading-relaxed text-slate-600">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-brand-700">Decisions after readouts</p>
              <h2
                id="oncology-article-clinical-heading"
                className="mt-2 text-pretty text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl"
              >
                From trial readouts to bedside decisions
              </h2>
            </div>
            <p>
              A surrogate shift on a forest plot is only the start. Teams still ask whether the effect size is clinically
              meaningful for this patient, whether toxicity trades off against the surrogate gain, and what happens if
              later survival data tell a different story.
            </p>
            <p>
              In shared decision-making, it can help to name the endpoint explicitly, note how mature the survival
              follow-up is, and agree what would change the plan if new evidence appears. That framing keeps surrogate-led
              approvals and label discussions grounded in ongoing learning, not a single press release.
            </p>
            <p>
              If you are on active treatment, bring your trial printout or pharmacy summary to visits and ask how your
              team interprets the same numbers in your tumour type and line of therapy.
            </p>
          </div>
          <div className="min-h-0 w-full md:h-full">
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl shadow-lg ring-1 ring-slate-200/80 md:aspect-auto md:h-full md:min-h-0">
              <img
                src={articleClinicalImage}
                alt="Laboratory pipette dispensing pink liquid into sample tubes in a blue rack, illustrating oncology drug research"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ArticleOncologySurrogateEndpoints;
